import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { makePrioStyles } from '../../../../../theme/utils';
import { VirtualTable } from '@prio365/prio365-react-library';
import { Column } from '@prio365/prio365-react-library/lib/VirtualTable/types/Column';
import {
  ContactImportProcessDto,
  ExternalBusinessUnitImportDto,
  ExternalCompanyImportDto,
  EMPTY_GUID,
  UpdateContactCompanyFromImportProcessDto,
} from '../../../../../models/ContactImportProcess';
import { VirtualListItemOnRowProps } from '@prio365/prio365-react-library/lib/VirtualList/components/VirtualListItem';
import ExternalCompanyDrawer from '../components/ExternalCompanyDrawer';
import ContactImporterFilterBar from '../components/ContactImporterFilterBar';
import { useCompaniesContext } from '../../../../companies/hooks/useCompaniesContext';
import { apiUpdateContactsFromImportProcess } from '../../../api';
const useStyles = makePrioStyles((theme) => ({
  root: { height: '100%' },
}));

const normalizeAction = (action: any): string => {
  const NUM_TO_STR: Record<number, string> = {
    0: 'notset', 1: 'skip', 2: 'updatemissing', 3: 'overrideexisting', 4: 'create',
  };
  if (typeof action === 'number') return NUM_TO_STR[action] || String(action);
  return String(action).toLowerCase();
};

const getActionDisplay = (value: any): { color: string; label: string } => {
  const v = normalizeAction(value);
  const map: Record<string, { color: string; label: string }> = {
    'notset': { color: '#fa8c16', label: 'Nicht festgelegt' },
    'skip': { color: '#8c8c8c', label: 'Überspringen' },
    'updatemissing': { color: '#1890ff', label: 'Fehlende aktualisieren' },
    'overrideexisting': { color: '#1890ff', label: 'Bestehende überschreiben' },
    'create': { color: '#52c41a', label: 'Erstellen' },
  };
  return map[v] || { color: '#8c8c8c', label: String(value) };
};

interface ExternalCompanyTabProps {
  contactImportProcess: ContactImportProcessDto | null;
  onProcessUpdate: (updatedProcess: ContactImportProcessDto) => void;
}

export const ExternalCompanyTab: React.FC<ExternalCompanyTabProps> = ({
  contactImportProcess,
  onProcessUpdate,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { getCompanyById } = useCompaniesContext(true);

  const [selectedCompany, setSelectedCompany] =
    useState<ExternalCompanyImportDto | null>(null);
  const [businessUnits, setBusinessUnits] = useState<
    ExternalBusinessUnitImportDto[]
  >([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [actionFilter, setActionFilter] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [selectedItems, setSelectedItems] = useState<ExternalCompanyImportDto[]>([]);

  const filteredCompanies = useMemo(() => {
    if (!contactImportProcess) return [];
    let items = contactImportProcess.externalCompanyImports ?? [];
    if (actionFilter !== null) {
      items = items.filter((c) => normalizeAction(c.action) === actionFilter);
    }
    if (searchText) {
      const lower = searchText.toLowerCase();
      items = items.filter(
        (c) =>
          c.legalName?.toLowerCase().includes(lower) ||
          c.displayName?.toLowerCase().includes(lower)
      );
    }
    return items;
  }, [contactImportProcess, actionFilter, searchText]);

  const handleRowClick: (
    item: ExternalCompanyImportDto
  ) => VirtualListItemOnRowProps<ExternalCompanyImportDto> = useCallback(
    (company) => ({
      onClick: () => {
        const companyBUs =
          (contactImportProcess?.externalBusinessUnitImports ?? []).filter(
            (bu) =>
              bu.externalCompanyImportId === company.externalCompanyImportId
          ) || [];
        setSelectedCompany(company);
        setBusinessUnits(companyBUs);
        setIsDrawerOpen(true);
      },
    }),
    [contactImportProcess?.externalBusinessUnitImports]
  );

  const getContactCountForCompany = useCallback(
    (companyId: string) =>
      (contactImportProcess?.externalContactImports ?? []).filter(
        (contact) => contact.externalCompanyImportId === companyId
      ).length || 0,
    [contactImportProcess?.externalContactImports]
  );

  const resolveCompanyName = useCallback(
    (existingCompanyId: string): string => {
      if (!existingCompanyId || existingCompanyId === EMPTY_GUID) return '-';
      const company = getCompanyById(existingCompanyId);
      return company?.shortName || company?.fullName || t('settings:companyImport.matchFound');
    },
    [getCompanyById, t]
  );

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedCompany(null);
    setBusinessUnits([]);
  };

  const handleBulkAction = async (action: string) => {
    if (!contactImportProcess || selectedItems.length === 0) return;

    const updateDto: UpdateContactCompanyFromImportProcessDto = {
      contactImportProcessId: contactImportProcess.contactImportProcessId,
      updateExternalCompanyImports: selectedItems.map((c) => ({
        externalCompanyImportId: c.externalCompanyImportId,
        legalName: c.legalName,
        displayName: c.displayName,
        notes: c.notes || '',
        commercialRegisterNumber: c.commercialRegisterNumber || '',
        commercialRegisterCity: c.commercialRegisterCity || '',
        legalForm: c.legalForm || '',
        vatNumber: c.vatNumber || '',
        existingCompanyId: c.existingCompanyId,
        description: c.description || '',
        action: action as any,
      })),
    };

    try {
      const result = await apiUpdateContactsFromImportProcess(
        contactImportProcess.contactImportProcessId,
        updateDto
      );
      if (result.result.ok) {
        const selectedIds = new Set(selectedItems.map((s) => s.externalCompanyImportId));
        const updatedProcess = {
          ...contactImportProcess,
          externalCompanyImports: (contactImportProcess.externalCompanyImports ?? []).map((c) =>
            selectedIds.has(c.externalCompanyImportId)
              ? { ...c, action: action as any }
              : c
          ),
        };
        onProcessUpdate(updatedProcess);
        setSelectedItems([]);
      }
    } catch (error) {
      console.error('Bulk update failed:', error);
    }
  };

  const columns: Column<ExternalCompanyImportDto>[] = [
    {
      id: 'legalName',
      alignSelf: true,
      width: 250,
      title: t('settings:companyImport.table.legalName'),
      accessor: 'legalName',
      Cell: (cellProps) => cellProps.value,
      sortingFn: (a, b) =>
        (a.legalName || '').localeCompare(b.legalName || ''),
    },
    {
      id: 'displayName',
      alignSelf: true,
      width: 200,
      title: t('settings:companyImport.table.displayName'),
      accessor: 'displayName',
      Cell: (cellProps) => cellProps.value,
      sortingFn: (a, b) =>
        (a.displayName || '').localeCompare(b.displayName || ''),
    },
    {
      id: 'existingCompanyId',
      alignSelf: true,
      width: 200,
      title: t('settings:companyImport.table.existingCompanyId'),
      accessor: 'existingCompanyId',
      Cell: (cellProps) => resolveCompanyName(cellProps.value),
      sortingFn: (a, b) =>
        resolveCompanyName(a.existingCompanyId).localeCompare(
          resolveCompanyName(b.existingCompanyId)
        ),
    },
    {
      id: 'contactCount',
      alignSelf: true,
      width: 80,
      title: t('settings:companyImport.table.contactCount'),
      accessor: 'externalCompanyImportId',
      Cell: (cellProps) => getContactCountForCompany(cellProps.value),
      sortingFn: (a, b) =>
        getContactCountForCompany(a.externalCompanyImportId) -
        getContactCountForCompany(b.externalCompanyImportId),
    },
    {
      id: 'description',
      alignSelf: true,
      width: 200,
      title: t('settings:companyImport.table.description'),
      accessor: 'description',
      Cell: (cellProps) => cellProps.value,
      sortingFn: (a, b) =>
        (a.description || '').localeCompare(b.description || ''),
    },
    {
      id: 'action',
      alignSelf: true,
      width: 150,
      title: t('settings:companyImport.table.action'),
      accessor: 'action',
      Cell: (cellProps) => {
        const { color, label } = getActionDisplay(cellProps.value);
        return (
          <span style={{
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            backgroundColor: color,
            color: '#fff',
          }}>
            {label}
          </span>
        );
      },
      sortingFn: (a, b) => normalizeAction(a.action).localeCompare(normalizeAction(b.action)),
    },
  ];

  if (!contactImportProcess) {
    return <p>{t('settings:companyImport.noData')}</p>;
  }

  return (
    <div className={classNames(classes.root)}>
      <ContactImporterFilterBar
        searchChangeHandler={(value) => setSearchText(typeof value === 'string' ? value : value?.target?.value ?? '')}
        actionFilter={actionFilter}
        onActionFilterChange={setActionFilter}
      />

      <VirtualTable<ExternalCompanyImportDto>
        columns={columns}
        data={filteredCompanies}
        id="external-company-table"
        resizable="absolute"
        rowsAreSelectable={true}
        onSelectionChange={setSelectedItems}
        selectedItems={selectedItems}
        onRow={handleRowClick}
        actionBarButtons={[
          {
            children: t('settings:companyImport.bulk.skip'),
            onClick: () => handleBulkAction('Skip'),
          },
          {
            children: t('settings:companyImport.bulk.create'),
            onClick: () => handleBulkAction('Create'),
          },
          {
            children: t('settings:companyImport.bulk.updateMissing'),
            onClick: () => handleBulkAction('UpdateMissing'),
          },
          {
            children: t('settings:companyImport.bulk.overrideExisting'),
            onClick: () => handleBulkAction('OverrideExisting'),
          },
        ] as any}
      />

      <ExternalCompanyDrawer
        visible={isDrawerOpen}
        onClose={closeDrawer}
        selectedCompany={selectedCompany}
        businessUnits={businessUnits}
        contacts={
          (contactImportProcess.externalContactImports ?? []).filter(
            (contact) =>
              contact.externalCompanyImportId ===
              selectedCompany?.externalCompanyImportId
          )
        }
        contactImportProcess={contactImportProcess}
        onProcessUpdate={onProcessUpdate}
      />
    </div>
  );
};
